/**
 * One trade, at full size.
 *
 * `docs/23` §5 called this the largest gap between AEGIS and the competitor,
 * and it is a layout gap rather than a data one: everything here was already
 * computed and had nowhere with enough room to show it. The journal's side
 * panel can hold a note and six figures. It cannot hold the excursion, the
 * execution sequence, the chart of the trade's own window, and three kinds of
 * tag at once, and cramming them in would have made the journal list worse
 * without making any of them good.
 *
 * ## The scrubber
 *
 * The execution list replays. Dragging the scrubber moves a clock across the
 * trade's life, and each execution lights up as the clock passes it — the one
 * idea `docs/23` takes directly from the competitor, and the place it differs
 * from them: prices ahead of the clock are **withheld**, not merely dimmed.
 * See `ExecutionList`.
 *
 * Parked at the end by default, which is the reviewing case. Moving it is a
 * deliberate act, so nothing is hidden from a trader who did not ask for it.
 */
import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { DEFAULT_CHART_STYLE, isChartStyle, type ChartStyle } from '@aegis/ui';
import { Chart } from '../components/Chart.js';
import { ExecutionList } from '../components/ExecutionList.js';
import { TagPicker } from '../components/TagPicker.js';
import { TradeAttachments } from '../components/TradeAttachments.js';
import { TradeContextPanel } from '../components/TradeContext.js';
import { EmptyState } from '../components/EmptyState.js';
import { Pnl } from '../components/panels.js';
import { Icon } from '../components/Icon.js';
import { Link } from '../lib/router.js';
import { zoneResolver } from '../lib/zone.js';
import {
  api, ApiError, fmtNum, fmtPct, fmtTime, fmtUsd,
  type Bar, type Instrument, type Trade,
} from '../lib/api.js';

const MAX_NOTES = 20_000;

/** `3h 42m`, or `18m 04s` for something short. Never a bare second count. */
function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m ${String(s % 60).padStart(2, '0')}s`;
}

export function TradeDetailScreen({ tradeId }: { tradeId: string }): JSX.Element {
  const [accountId, setAccountId] = useState<string | null>(null);
  const [trade, setTrade] = useState<Trade | null>(null);
  const [instrument, setInstrument] = useState<Instrument | null>(null);
  const [instruments, setInstruments] = useState<readonly Instrument[]>([]);
  const [bars, setBars] = useState<Bar[]>([]);
  /**
   * Carried from the bars response, not assumed to be zero.
   *
   * The non-negotiable is that `delayedByMs` travels with the data to the
   * render layer so the staleness badge comes from the data itself. A detail
   * screen hard-coding 0 because "it's historical anyway" is exactly the
   * separate flag that rule exists to prevent — the last bar of a delayed feed
   * is as delayed here as it is on the chart screen.
   */
  const [delayedByMs, setDelayedByMs] = useState(0);
  const [tzPref, setTzPref] = useState<string | null>(null);
  /*
    The review chart follows the same preference as the live one.

    A trader who reads hollow candles everywhere should not be handed filled
    ones on the screen where they are deciding what a trade did. Preferences
    are already fetched here for the timezone, so this costs nothing.
  */
  const [chartStyle, setChartStyle] = useState<ChartStyle>(DEFAULT_CHART_STYLE);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  const [notes, setNotes] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Null parks the clock at the end — everything visible. */
  const [clock, setClock] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setNotFound(false);

    void (async () => {
      try {
        const [accounts, insts, prefs] = await Promise.all([
          api.accounts(), api.instruments(), api.preferences(),
        ]);
        if (!live) return;
        setInstruments(insts.instruments);
        setTzPref(prefs.timezone);
        setChartStyle(isChartStyle(prefs.chartStyle) ? prefs.chartStyle : DEFAULT_CHART_STYLE);

        // The trade id does not carry its account, so this looks across the
        // user's accounts rather than guessing the first one. A trader with a
        // funded account and an evaluation has two, and landing on the wrong
        // one would render "not found" for a trade that exists.
        let found: Trade | null = null;
        let foundIn: string | null = null;
        for (const a of accounts.accounts) {
          const { trades, openTrades } = await api.trades(a.id);
          found = [...trades, ...openTrades].find((t) => t.id === tradeId) ?? null;
          if (found) { foundIn = a.id; break; }
        }
        if (!live) return;
        if (!found) { setNotFound(true); return; }
        setTrade(found);
        // Carried out of the search rather than re-derived: the context
        // endpoint is account-scoped, and re-scanning to find the account
        // again would be a second pass over every trade the user owns.
        setAccountId(foundIn);
        setInstrument(insts.instruments.find((i) => i.root === found!.symbol) ?? null);

        const b = await api.bars(found.symbol, '5m', 400);
        if (live) { setBars(b.bars); setDelayedByMs(b.delayedByMs); }
      } catch {
        if (live) setNotFound(true);
      } finally {
        if (live) setLoading(false);
      }
    })();

    return () => { live = false; };
  }, [tradeId]);

  useEffect(() => {
    let live = true;
    void api.journal(tradeId)
      .then((e) => {
        if (!live) return;
        setNotes(e.notes ?? '');
        setRating(e.rating ?? null);
        setTagIds([...(e.tagIds ?? [])]);
      })
      .catch((e) => {
        // 404 is "no note yet", the common case. Tags still have to be loaded.
        if (!live) return;
        if (!(e instanceof ApiError) || e.status !== 404) setError('Could not load this entry.');
        void api.tradeTags()
          .then((m) => { if (live) setTagIds([...(m.trades[tradeId] ?? [])]); })
          .catch(() => { /* the picker reports its own failure */ });
      });
    return () => { live = false; };
  }, [tradeId]);

  const zone = useMemo(
    () => zoneResolver(tzPref, instruments, trade ? [trade.symbol] : []),
    [tzPref, instruments, trade],
  );

  const window = useMemo(() => {
    if (!trade) return null;
    const from = Date.parse(trade.openedAt);
    const to = trade.closedAt ? Date.parse(trade.closedAt) : Date.now();
    return { from, to };
  }, [trade]);

  /**
   * Bars covering the trade's own life, plus context either side.
   *
   * Context matters: a chart cropped exactly to the trade shows the entry at
   * the left edge, which tells the trader nothing about what preceded it.
   */
  const tradeBars = useMemo(() => {
    if (!window || bars.length === 0) return bars;
    const pad = 20 * 5 * 60; // twenty 5-minute bars, in seconds
    const from = Math.floor(window.from / 1000) - pad;
    const to = Math.floor(window.to / 1000) + pad;
    const inside = bars.filter((b) => b.time >= from && b.time <= to);
    // Falling back to the full series is better than an empty chart when the
    // trade predates the bars the feed will return.
    return inside.length > 0 ? inside : bars;
  }, [bars, window]);

  async function saveTags(next: string[]): Promise<void> {
    const previous = tagIds;
    setTagIds(next);
    try {
      await api.setTradeTags(tradeId, next);
    } catch {
      setTagIds(previous);
      setError('Could not save those tags.');
    }
  }

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await api.saveJournal(tradeId, { notes, rating, emotions: [] });
      setSaved(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="scroll-area"><p className="dim">Loading…</p></div>;

  if (notFound || !trade) {
    return (
      <div className="scroll-area">
        <EmptyState icon="journal" title="No such trade">
          <p>
            This trade is not in any of your accounts. It may belong to an
            account that has been disconnected.
          </p>
          <Link to="/journal" className="btn btn-secondary">Back to the journal</Link>
        </EmptyState>
      </div>
    );
  }

  const g = trade.excursion;
  const held = trade.closedAt
    ? fmtDuration(Date.parse(trade.closedAt) - Date.parse(trade.openedAt))
    : 'open';

  return (
    <div className="scroll-area trade-detail">
      <div className="section-head">
        <div>
          <Link to="/journal" className="back-link">
            <Icon name="chevronLeft" size="sm" /> Journal
          </Link>
          <h2 className="detail-title">
            {trade.symbol}
            <span className={`dir dir-${trade.direction}`}>
              {trade.direction === 'long' ? 'LONG' : 'SHORT'}
            </span>
            <span className="mono dim">{Number(trade.quantity)}</span>
          </h2>
        </div>
        <div className="detail-headline">
          <div className="detail-pnl"><Pnl value={trade.netPnl} /></div>
          <div className="dim">net · {held}</div>
        </div>
      </div>

      <div className="detail-grid">
        <aside className="detail-stats">
          <h3 className="panel-h">THE TRADE</h3>
          <dl className="kv kv-tight">
            <dt>Opened</dt>
            <dd className="mono">{fmtTime(trade.openedAt, zone.of(trade.symbol))}</dd>
            <dt>Closed</dt>
            <dd className="mono">
              {trade.closedAt ? fmtTime(trade.closedAt, zone.of(trade.symbol)) : 'still open'}
            </dd>
            {zone.label && (
              <>
                <dt>Times in</dt>
                <dd className="mono dim">{zone.label}</dd>
              </>
            )}
            <dt>Entry</dt><dd className="mono">{fmtNum(trade.avgEntryPrice)}</dd>
            <dt>Exit</dt>
            <dd className="mono">{trade.avgExitPrice ? fmtNum(trade.avgExitPrice) : '—'}</dd>
            <dt>Gross</dt><dd><Pnl value={trade.grossPnl} /></dd>
            <dt>Commissions</dt><dd className="mono dim">{fmtUsd(trade.commissions)}</dd>
            <dt>Fees</dt><dd className="mono dim">{fmtUsd(trade.fees)}</dd>
            <dt>Net</dt><dd><Pnl value={trade.netPnl} /></dd>
            <dt>Matching</dt><dd className="mono dim">{trade.matchingMethod}</dd>
          </dl>

          <h3 className="panel-h">RISK</h3>
          <dl className="kv kv-tight">
            <dt>Risk basis</dt>
            <dd className="mono">{trade.riskBasis ? fmtUsd(trade.riskBasis) : 'not recorded'}</dd>
            <dt>R-multiple</dt>
            {/* Null, never guessed. `reconstruct.ts` will not invent a risk
                basis, and printing "0R" here would be inventing one. */}
            <dd className="mono">{trade.rMultiple ? fmtNum(trade.rMultiple, 2) : '—'}</dd>
          </dl>

          <h3 className="panel-h">EXCURSION</h3>
          {g ? (
            <dl className="kv kv-tight">
              <dt>Ran to</dt><dd><Pnl value={g.mfe} /></dd>
              <dt>Drew down to</dt><dd><Pnl value={g.mae} /></dd>
              <dt>Captured</dt>
              <dd className="mono">
                {g.capture ? fmtPct(g.capture) : <span className="dim">never went favourable</span>}
              </dd>
              <dt>Heat</dt>
              <dd className="mono">
                {g.heat
                  ? `${fmtNum(g.heat, 2)}× the result`
                  : <span className="dim">never went adverse</span>}
              </dd>
            </dl>
          ) : (
            <p className="field-hint">
              Bar data does not cover this trade, so its excursion is unknown.
              AEGIS reports that rather than a zero that would read as "took no heat".
            </p>
          )}
        </aside>

        <section className="detail-chart">
          <Chart
            symbol={trade.symbol}
            instrument={instrument}
            bars={tradeBars}
            analysis={null}
            trades={[trade]}
            showAnnotations={false}
            timeframe="5m"
            style={chartStyle}
            delayedByMs={delayedByMs}
          />
        </section>
      </div>

      <div className="detail-lower">
        {/*
          Reconstructed from bars at request time, so it works on imported
          history as well as synced history — see packages/core journal/context.
        */}
        {accountId && (
          <section className="detail-context">
            <TradeContextPanel
              accountId={accountId}
              tradeId={tradeId}
              precision={instrument?.displayPrecision ?? 2}
            />
          </section>
        )}

        <section className="detail-execs">
          {window && trade.executions.length > 0 && (
            <div className="scrubber">
              <label className="field-label" htmlFor="trade-scrub">Replay this trade</label>
              <input
                id="trade-scrub"
                type="range"
                min={window.from}
                max={window.to}
                step={Math.max(1000, Math.round((window.to - window.from) / 500))}
                value={clock ?? window.to}
                onChange={(e) => setClock(Number(e.target.value))}
              />
              <div className="scrubber-foot">
                <span className="mono dim">
                  {fmtTime(new Date(clock ?? window.to).toISOString(), zone.of(trade.symbol))}
                </span>
                {clock !== null && (
                  <button type="button" className="btn-ghost" onClick={() => setClock(null)}>
                    Show all
                  </button>
                )}
              </div>
            </div>
          )}
          <ExecutionList
            executions={trade.executions}
            clockMs={clock}
            zone={zone.of(trade.symbol)}
            live={clock === null}
          />
          {trade.executions.length === 0 && (
            <p className="field-hint">
              {trade.isOpen
                ? 'Executions are matched when the position closes, so an open trade has none yet.'
                : 'No executions recorded for this trade.'}
            </p>
          )}
        </section>

        <section className="detail-note">
          <h3 className="panel-h">HOW YOU SAW IT</h3>
          <p className="field-hint">
            Everything above is reconstructed from broker fills and is not
            editable. What you own is the interpretation.
          </p>

          <TagPicker value={tagIds} onChange={(next) => void saveTags(next)} />

          <div className="field">
            <span className="field-label">Rating</span>
            <div className="rating" role="group" aria-label="Trade rating, 1 to 5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`rating-btn compact ${rating !== null && n <= rating ? 'on' : ''}`}
                  aria-pressed={rating !== null && n <= rating}
                  aria-label={`${n} out of 5`}
                  onClick={() => { setRating(rating === n ? null : n); setSaved(false); }}
                >
                  {rating !== null && n <= rating ? '★' : '☆'}
                </button>
              ))}
            </div>
          </div>

          <label className="field">
            <span className="field-label">Notes</span>
            <textarea
              className="input textarea"
              rows={10}
              value={notes}
              maxLength={MAX_NOTES}
              onChange={(e) => { setNotes(e.target.value); setSaved(false); }}
              placeholder="What did you see? What would you do differently?"
            />
            <span className="field-hint">
              {notes.length.toLocaleString('en-US')} / {MAX_NOTES.toLocaleString('en-US')}
            </span>
          </label>

          {error && <div className="form-error" role="alert">{error}</div>}
          {saved && <div className="form-ok" role="status">Saved.</div>}

          <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save note'}
          </button>

          <TradeAttachments tradeId={tradeId} />
        </section>
      </div>
    </div>
  );
}
